import { useEffect, useState } from "react"
import React from "react"
import { useParams } from "react-router-dom"
import {
  transferPartners,
  hotelPartners,
  airlinePartners,
  partnerDetails,
} from "../data/TransferPartners"

function CardDetails() {
  const [cards, setCards] = useState([])
  const { id } = useParams()

  useEffect(() => {
    // Dynamic import based on environment variable
    import(`../data/CardData-${process.env.REACT_APP_LANGUAGE}.jsx`)
      .then(module => setCards(module.default))
      .catch(error => console.error("Failed to load card data:", error))
  }, [])

  const card = cards.find(card => card.id === parseInt(id, 10))

  if (!card) {
    return <p className="text-center text-xl text-red-600">Card not found!</p>
  }

  return (
    <div className="container mx-auto px-6 py-12">
      <div className="flex flex-col lg:flex-row items-center lg:items-start gap-8">
        <img
          src={card.image}
          alt={card.name}
          className="w-full max-w-md rounded-lg shadow-md"
        />
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">
            {card.name}
            {card.hotOffer && (
              <span className="inline-block bg-red-500 text-white text-sm font-semibold px-3 py-1 rounded-full ml-2 align-middle">
                🔥 Hot Offer
              </span>
            )}
          </h1>{" "}
          <p className="text-lg text-gray-600 mb-4">
            <span className="font-semibold text-gray-800">Annual Fee:</span> $
            {card.annualFee}
          </p>
          <a
            href={card.applyLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-6 py-3 bg-blue-600 text-white font-semibold rounded-md shadow-md hover:bg-blue-700 transition"
          >
            Apply for this Card
          </a>
        </div>
      </div>

      <div className="mt-12 space-y-8">
        <section>
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">
            Main Benefits
          </h2>
          <ul className="space-y-4">
            {card.mainBenefit.map((benefit, index) => (
              <li key={index} className="flex items-start space-x-3">
                <span className="text-green-500">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </span>
                <p className="text-lg text-gray-700">{benefit}</p>
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">
            Why Keep This Card?
          </h2>
          <div className="space-y-4">
            {card.whyKeep.map((paragraph, index) => (
              <p
                key={index}
                className="text-lg text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: paragraph }}
              ></p>
            ))}
          </div>
        </section>

        {/* Transfer Partners Section */}
        {transferPartners[card.id] && transferPartners[card.id].length > 0 && (
          <section>
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              Transfer Partners
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Hotel Partners */}
              {transferPartners[card.id].filter((p) =>
                hotelPartners.includes(p)
              ).length > 0 && (
                <div className="bg-yellow-50 p-4 rounded-lg shadow-sm border border-yellow-200">
                  <h3 className="text-lg font-bold text-yellow-800 mb-2 flex items-center">
                    <span className="mr-2">🏨</span> Hotel Partners
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {transferPartners[card.id]
                      .filter((p) => hotelPartners.includes(p))
                      .sort()
                      .map((partner) => {
                        const url =
                          partnerDetails[partner] && partnerDetails[partner].url
                        return url ? (
                          <a
                            key={partner}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1 bg-white text-blue-600 text-sm font-medium rounded-full border border-gray-200 shadow-sm hover:bg-gray-50 transition-colors"
                          >
                            {partner}
                          </a>
                        ) : (
                          <span
                            key={partner}
                            className="px-3 py-1 bg-white text-gray-700 text-sm font-medium rounded-full border border-gray-200 shadow-sm"
                          >
                            {partner}
                          </span>
                        )
                      })}
                  </div>
                </div>
              )}

              {/* Airline Partners */}
              {transferPartners[card.id].filter((p) =>
                airlinePartners.includes(p)
              ).length > 0 && (
                <div className="bg-blue-50 p-4 rounded-lg shadow-sm border border-blue-200">
                  <h3 className="text-lg font-bold text-blue-800 mb-2 flex items-center">
                    <span className="mr-2">✈️</span> Airline Partners
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {transferPartners[card.id]
                      .filter((p) => airlinePartners.includes(p))
                      .sort()
                      .map((partner) => {
                        const url =
                          partnerDetails[partner] && partnerDetails[partner].url
                        return url ? (
                          <a
                            key={partner}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1 bg-white text-blue-600 text-sm font-medium rounded-full border border-gray-200 shadow-sm hover:bg-gray-50 transition-colors"
                          >
                            {partner}
                          </a>
                        ) : (
                          <span
                            key={partner}
                            className="px-3 py-1 bg-white text-gray-700 text-sm font-medium rounded-full border border-gray-200 shadow-sm"
                          >
                            {partner}
                          </span>
                        )
                      })}
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {card.annualFee > 0 ? (
          <section>
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              Yearly Cost Breakdown
            </h2>
            <div className="bg-gray-50 rounded-lg shadow-md p-6">
              <div className="space-y-4">
                {card.costBreakdown.map((item, index) => (
                  <div key={index}>
                    {/* Cost Description and Value */}
                    <div className="flex justify-between">
                      <span
                        className={`${
                          index === 0
                            ? "text-gray-800 font-bold"
                            : index === card.costBreakdown.length - 1
                            ? "text-gray-800 font-bold"
                            : "text-gray-700"
                        }`}
                      >
                        {item.description}
                      </span>
                      <span
                        className={`${
                          item.amount > 0
                            ? "text-gray-800"
                            : index === card.costBreakdown.length - 1
                            ? "text-blue-600 font-bold"
                            : "text-green-600"
                        }`}
                      >
                        {item.amount > 0
                          ? `$${item.amount}`
                          : `- $${Math.abs(item.amount)}`}
                      </span>
                    </div>

                    {/* Progress Bar */}
                    {index !== card.costBreakdown.length - 1 && (
                      <div className="w-full bg-gray-200 rounded-full h-4 mt-2">
                        <div
                          className={`${
                            index === 0 ? "bg-blue-600" : "bg-green-600"
                          } h-4 rounded-full`}
                          style={{
                            width: `${
                              (Math.abs(item.amount) /
                                card.costBreakdown[0].amount) *
                              100
                            }%`,
                          }}
                        ></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        ) : (
          <></>
        )}

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">
            Bonus Comparison
          </h2>
          <div className="flex flex-wrap gap-6">
            <div className="flex-1 min-w-[250px] bg-blue-50 rounded-lg shadow-md p-6">
              <h3 className="text-lg font-bold text-blue-700">Current Bonus</h3>
              <p className="text-lg text-gray-700">{card.bonus}</p>
            </div>
            <div className="flex-1 min-w-[250px] bg-green-50 rounded-lg shadow-md p-6">
              <h3 className="text-lg font-bold text-green-700">
                Historic Highest Bonus
              </h3>
              <p className="text-lg text-gray-700">
                {card.historicHighestBonus}
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

export default CardDetails
